// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import { isExtensionExecutorsStack } from '@/shared/lib/errors/extension-frames';
import { MESSAGE_NOISE_RULES } from '@/shared/lib/errors/noise-rules';
import * as Sentry from '@sentry/nextjs';
import posthog from 'posthog-js';

type SentryEvent = Parameters<
  NonNullable<Parameters<typeof Sentry.init>[0]>['beforeSend'] & object
>[0];

type NoiseRule = {
  id: string;
  match: (event: SentryEvent) => boolean;
};

// Events matching any rule are tagged `signal: 'noise'` + `noise_rule: <id>`
// so they remain visible for spike detection but can be filtered in dashboards.
// Add new rules here — do not grow the `beforeSend` body.
const NOISE_RULES: readonly NoiseRule[] = [
  {
    // CDN CORS rejections from non-standard browsers (e.g. DuckDuckGo Mobile)
    // whose Origin header isn't in BunnyCDN's allow list.
    id: 'invalid-origin',
    match: (e) => e.exception?.values?.[0]?.value?.toLowerCase() === 'invalid origin',
  },
  {
    // Shaka player teardown race: destroy() after unmount rejects a pending
    // Shaka promise (7xxx PLAYER category) with no handler. Not actionable.
    id: 'shaka-teardown-rejection',
    match: (e) => {
      const ex = e.exception?.values?.[0];
      return (
        ex?.mechanism?.type === 'onunhandledrejection' && /Shaka Error 7\d{3}/.test(ex?.value ?? '')
      );
    },
  },
  {
    // Outlook SafeLinks / browser-extension injection: these browsers inject a
    // script that posts "Object Not Found Matching Id:N, MethodName:update,
    // ParamCount:N" as an unhandled rejection against our window. Not our code.
    id: 'object-not-found-extension',
    match: (e) => {
      const ex = e.exception?.values?.[0];
      return /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/.test(
        ex?.value ?? ''
      );
    },
  },
  {
    // A desktop Chrome/Edge extension's executor script throws "Cannot read
    // properties of undefined (reading 'M_ID')" on any page (Sentry FS/FT,
    // ~11 users). Every frame is the extension's, none is ours.
    id: 'extension-executors',
    match: (e) => isExtensionExecutorsStack(e.exception?.values?.[0]?.stacktrace?.frames),
  },
  // Message-based rules shared with the PostHog sink (noise-rules.ts) — the
  // same classes get tagged identically in both error trackers.
  ...MESSAGE_NOISE_RULES.map((rule): NoiseRule => ({
    id: rule.id,
    match: (e) => rule.test(e.exception?.values?.[0]?.value ?? ''),
  })),
];

// Skip Sentry in local dev — the SDK runtime (replay recorder, tracing, log
// transport) adds per-page overhead even though the build plugin is already
// skipped in next.config.ts. NEXT_PUBLIC_SENTRY_FORCE=1 re-enables it when
// testing Sentry itself (same pattern as NEXT_PUBLIC_CRISP_FORCE).
const sentryEnabled =
  process.env.NODE_ENV !== 'development' || process.env.NEXT_PUBLIC_SENTRY_FORCE === '1';

if (sentryEnabled) {
  Sentry.init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

    release: process.env.NEXT_PUBLIC_BUILD_ID,

    // Replay is added later by addReplayWhenIdle(), not here.
    integrations: [
      posthog.sentryIntegration({
        ...(process.env.NEXT_PUBLIC_SENTRY_ORG
          ? { organization: process.env.NEXT_PUBLIC_SENTRY_ORG }
          : {}),
        ...(process.env.NEXT_PUBLIC_SENTRY_PROJECT_ID
          ? { projectId: Number(process.env.NEXT_PUBLIC_SENTRY_PROJECT_ID) }
          : {}),
        // Disable sending $exception events to PostHog — prevents anonymous person
        // profiles from being created when errors fire before posthog.identify()
        sendExceptionsToPostHog: false,
      }),
    ],

    // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
    // Dev only runs when forced via NEXT_PUBLIC_SENTRY_FORCE; keep its rate low
    tracesSampleRate: process.env.NODE_ENV === 'development' ? 0.2 : 0.1,
    // Enable logs to be sent to Sentry
    enableLogs: true,

    // Define how likely Replay events are sampled. Both rates stay on init:
    // the integration added later by addReplayWhenIdle() reads them from here.
    replaysSessionSampleRate: 0.1,

    // Define how likely Replay events are sampled when an error occurs.
    replaysOnErrorSampleRate: 1.0,

    // Enable sending user PII (Personally Identifiable Information)
    // https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/options/#sendDefaultPii
    sendDefaultPii: true,

    beforeSend(event) {
      const matched = NOISE_RULES.find((rule) => rule.match(event));
      if (matched) {
        event.level = 'warning';
        event.tags = { ...event.tags, signal: 'noise', noise_rule: matched.id };
      }

      try {
        const sessionReplayUrl = posthog.get_session_replay_url?.({
          withTimestamp: true,
        });
        if (sessionReplayUrl) {
          event.tags = { ...event.tags, 'posthog.replay_url': sessionReplayUrl };
        }
      } catch {
        // PostHog may not be initialized yet — let event flow without tags
      }

      const span = Sentry.getActiveSpan();
      const traceId = span?.spanContext().traceId;
      if (traceId) {
        event.tags = { ...event.tags, trace_id: traceId };
      }

      return event;
    },
  });

  addReplayWhenIdle();
}

// replaysOnErrorSampleRate 1.0 keeps Replay recording (buffer mode) in every
// session, and its first full-DOM snapshot was 760 ms of the home page's load
// task at 4x CPU (2026-10-19). Starting it once the page is loaded and idle
// keeps error replays for everything after; errors in the first seconds lose
// theirs (~3 render-crash sessions/week, most also have a PostHog replay).
// Not removed outright: PostHog replay still misses 86 of 360 error sessions.
function addReplayWhenIdle() {
  const add = () =>
    Sentry.addIntegration(Sentry.replayIntegration({ maskAllInputs: true, blockAllMedia: true }));
  const whenIdle = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(add, { timeout: 5000 });
    } else {
      setTimeout(add, 2000);
    }
  };
  if (document.readyState === 'complete') {
    whenIdle();
  } else {
    window.addEventListener('load', whenIdle, { once: true });
  }
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
